/**
 * The sections of a Shopify integration's Mapping tab (MOT-278, design "Mapping tab v3"): one left
 * menu, one panel at a time, the open one in the URL as `?tab=mapping&section=<key>`.
 *
 * Type & category: field owners and the four decision steps (Store Taxonomy, Name Rules, Motzu
 * Classification Overrides, Unmatched Product Rules). Fields & listings: spec metafields and listing
 * matches. Before v3 those were three tabs (MOT-252): Classification, Metafields and Catalog Matching.
 * Their links still work; `integrationDetailTabs.ts` rewrites them onto a section here.
 */
export const TYPE_CATEGORY_SECTIONS = ['owners', 'taxonomy', 'name-rules', 'category-map', 'fallback'] as const;
export const MAPPING_SECTIONS = [...TYPE_CATEGORY_SECTIONS, 'spec-metafields', 'listing-matches'] as const;

export type TypeCategorySection = (typeof TYPE_CATEGORY_SECTIONS)[number];
export type MappingSection = (typeof MAPPING_SECTIONS)[number];

/**
 * A `?section=` value a link may carry: a section, or `try`, the Classification jump link to Try a
 * product (MOT-252). Try a product is no longer a section of its own; it sits beside the Field
 * ownership panel, so `try` opens that panel and scrolls to the tester.
 */
export type MappingSectionLink = MappingSection | 'try';

export const DEFAULT_MAPPING_SECTION: MappingSection = 'owners';

export const isMappingSection = (value: unknown): value is MappingSection =>
  typeof value === 'string' && (MAPPING_SECTIONS as readonly string[]).includes(value);

export const isMappingSectionLink = (value: unknown): value is MappingSectionLink =>
  value === 'try' || isMappingSection(value);

export const isTypeCategorySection = (value: unknown): value is TypeCategorySection =>
  typeof value === 'string' && (TYPE_CATEGORY_SECTIONS as readonly string[]).includes(value);

/** The menu item a link opens: `try` is the Field ownership panel, a missing section the default. */
export const menuSectionOf = (link: MappingSectionLink | null | undefined): MappingSection =>
  !link || link === 'try' ? DEFAULT_MAPPING_SECTION : link;

/** The three editors the sections belong to; the flag-off layout (`shopify_mapping_v2` off) shows one per view. */
export type MappingView = 'type' | 'specs' | 'matches';

export const mappingViewOf = (link: MappingSectionLink | null | undefined): MappingView => {
  const section = menuSectionOf(link);
  if (section === 'spec-metafields') return 'specs';
  if (section === 'listing-matches') return 'matches';
  return 'type';
};

/** The section each view opens on, for the flag-off menu, which has one item per view. */
export const SECTION_OF_VIEW: Record<MappingView, MappingSection> = {
  type: 'owners',
  specs: 'spec-metafields',
  matches: 'listing-matches',
};
